import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { apiClient } from '@/api/client';
import type { ExamQuestion, ExamSubmissionResponse } from '@/types';

type ExamStatus = 'not_started' | 'loading' | 'in_progress' | 'submitted' | 'completed' | 'expired';

interface ExamState {
  questions: ExamQuestion[];
  answers: Record<number, number>;
  currentQuestion: number;
  totalQuestions: number;
  timeRemaining: number;
  examDuration: number;
  startTime: string | null;
  endTime: string | null;
  status: ExamStatus;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  score: number | null;
  correctAnswers: number | null;
  autoSaveEnabled: boolean;
  lastSaved: number | null;
  hasUnsavedChanges: boolean;
}

interface ExamActions {
  loadQuestions: (sessionId: string) => Promise<void>;
  setAnswer: (questionId: number, answer: number) => void;
  submitExam: (sessionId: string) => Promise<ExamSubmissionResponse>;
  setCurrentQuestion: (question: number) => void;
  startTimer: (duration: number) => void;
  stopTimer: () => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  resetExam: () => void;
  setError: (error: string | null) => void;
  saveProgress: (sessionId: string) => Promise<void>;
  enableAutoSave: (sessionId: string, interval?: number) => void;
  disableAutoSave: () => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  goToQuestion: (questionNumber: number) => void;
  getProgress: () => { answered: number; total: number; percentage: number };
}

type ExamStore = ExamState & ExamActions;

let timerInterval: NodeJS.Timeout | null = null;
let autoSaveInterval: NodeJS.Timeout | null = null;
let isPaused = false;

export const useExamStore = create<ExamStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        questions: [],
        answers: {},
        currentQuestion: 0,
        totalQuestions: 0,
        timeRemaining: 0,
        examDuration: 0,
        startTime: null,
        endTime: null,
        status: 'not_started',
        isLoading: false,
        isSubmitting: false,
        error: null,
        score: null,
        correctAnswers: null,
        autoSaveEnabled: false,
        lastSaved: null,
        hasUnsavedChanges: false,

        // Actions
        loadQuestions: async (sessionId: string) => {
          set({ isLoading: true, error: null, status: 'loading' });
          
          try {
            console.log('📝 Loading exam questions for session:', sessionId);
            const response = await apiClient.getExamQuestions(sessionId);
            console.log('📝 Exam questions response:', response);
            
            if (response.success && response.data) {
              const { questions, sessionInfo } = response.data;
              
              if (!questions || questions.length === 0) {
                throw new Error('No questions available for this exam');
              }
              
              console.log('📝 Loaded', questions.length, 'questions');
              
              set({
                questions,
                totalQuestions: questions.length,
                startTime: sessionInfo.startTime,
                status: 'in_progress',
                isLoading: false,
                error: null,
              });
              
              // Start with 30 minutes by default (can be configured)
              get().startTimer(30 * 60); // 30 minutes in seconds
            } else {
              const errorMsg = response.message || 'No exam data received from server';
              console.error('❌ Failed to load exam questions:', errorMsg);
              throw new Error(errorMsg);
            }
          } catch (error) {
            console.error('❌ Error loading exam questions:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to load exam';
            set({
              isLoading: false,
              error: errorMessage,
              status: 'not_started',
            });
            throw error;
          }
        },

        setAnswer: (questionId: number, answer: number) => {
          set(state => ({
            answers: { ...state.answers, [questionId]: answer },
            hasUnsavedChanges: true,
          }));
        },

        submitExam: async (sessionId: string) => {
          const { answers, questions, isSubmitting } = get();
          
          if (isSubmitting) return Promise.reject(new Error('Exam is already being submitted'));
          
          set({ isSubmitting: true, error: null });
          
          try {
            // Stop timer and auto-save
            get().stopTimer();
            get().disableAutoSave();
            
            const answersByQuestionId = Object.fromEntries(
              Object.entries(answers).map(([questionIndex, answer]) => {
                const question = questions[Number(questionIndex)];
                return [question?.id ?? Number(questionIndex) + 1, answer];
              })
            );
            const response = await apiClient.submitExam(sessionId, answersByQuestionId);
            
            if (response.success) {
              set({
                status: 'completed',
                endTime: new Date().toISOString(),
                score: response.score,
                correctAnswers: response.correctAnswers,
                isSubmitting: false,
                hasUnsavedChanges: false,
              });
              
              return response;
            } else {
              throw new Error('Failed to submit exam');
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Submission failed';
            set({
              isSubmitting: false,
              error: errorMessage,
            });
            throw error;
          }
        },

        setCurrentQuestion: (question: number) => {
          const { totalQuestions } = get();
          if (question >= 0 && question < totalQuestions) {
            set({ currentQuestion: question });
          }
        },

        startTimer: (duration: number) => {
          get().stopTimer(); // Clear any existing timer
          
          set({
            timeRemaining: duration,
            examDuration: duration,
          });
          
          isPaused = false;
          
          timerInterval = setInterval(() => {
            if (isPaused) return;
            
            const { timeRemaining, status } = get();
            
            if (timeRemaining <= 0 || status !== 'in_progress') {
              get().stopTimer();
              if (status === 'in_progress') {
                set({ status: 'expired' });
                // Auto-submit when time expires
                // Note: In a real implementation, you'd get sessionId from a prop or context
                // For now, we'll just set the status to expired
                console.warn('Exam time expired - auto-submit needed');
              }
              return;
            }
            
            set({ timeRemaining: timeRemaining - 1 });
          }, 1000);
        },

        stopTimer: () => {
          if (timerInterval) {
            clearInterval(timerInterval);
            timerInterval = null;
          }
          isPaused = false;
        },

        pauseTimer: () => {
          isPaused = true;
        },

        resumeTimer: () => {
          isPaused = false;
        },

        resetExam: () => {
          get().stopTimer();
          get().disableAutoSave();
          
          set({
            questions: [],
            answers: {},
            currentQuestion: 0,
            totalQuestions: 0,
            timeRemaining: 0,
            examDuration: 0,
            startTime: null,
            endTime: null,
            status: 'not_started',
            isLoading: false,
            isSubmitting: false,
            error: null,
            score: null,
            correctAnswers: null,
            lastSaved: null,
            hasUnsavedChanges: false,
          });
        },

        setError: (error: string | null) => {
          set({ error });
        },

        saveProgress: async (sessionId: string) => {
          const { answers, hasUnsavedChanges } = get();
          
          if (!hasUnsavedChanges) return;
          
          try {
            // In a real implementation, you'd have an API endpoint for saving progress
            // For now, we'll just mark as saved
            console.log('Saving exam progress:', { sessionId, answers });
            
            set({
              lastSaved: Date.now(),
              hasUnsavedChanges: false,
            });
          } catch (error) {
            console.error('Failed to save exam progress:', error);
          }
        },

        enableAutoSave: (sessionId: string, interval: number = 30000) => {
          get().disableAutoSave(); // Clear any existing auto-save
          
          set({ autoSaveEnabled: true });
          
          autoSaveInterval = setInterval(() => {
            const { hasUnsavedChanges, status } = get();
            if (hasUnsavedChanges && status === 'in_progress') {
              get().saveProgress(sessionId);
            }
          }, interval);
        },

        disableAutoSave: () => {
          if (autoSaveInterval) {
            clearInterval(autoSaveInterval);
            autoSaveInterval = null;
          }
          set({ autoSaveEnabled: false });
        },

        nextQuestion: () => {
          const { currentQuestion, totalQuestions } = get();
          if (currentQuestion < totalQuestions - 1) {
            set({ currentQuestion: currentQuestion + 1 });
          }
        },

        previousQuestion: () => {
          const { currentQuestion } = get();
          if (currentQuestion > 0) {
            set({ currentQuestion: currentQuestion - 1 });
          }
        },

        goToQuestion: (questionNumber: number) => {
          const { totalQuestions } = get();
          if (questionNumber >= 0 && questionNumber < totalQuestions) {
            set({ currentQuestion: questionNumber });
          }
        },

        getProgress: () => {
          const { answers, totalQuestions } = get();
          const answered = Object.keys(answers).length;
          const percentage = totalQuestions > 0 ? Math.round((answered / totalQuestions) * 100) : 0;
          
          return {
            answered,
            total: totalQuestions,
            percentage,
          };
        },
      }),
      {
        name: 'quizsecure-exam',
        partialize: (state) => ({
          answers: state.answers,
          currentQuestion: state.currentQuestion,
          startTime: state.startTime,
          timeRemaining: state.timeRemaining,
          examDuration: state.examDuration,
          lastSaved: state.lastSaved,
          hasUnsavedChanges: state.hasUnsavedChanges,
        }),
      }
    ),
    { name: 'ExamStore' }
  )
);

// Utility functions for time formatting
export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const getTimeColor = (timeRemaining: number, examDuration: number): string => {
  const percentage = (timeRemaining / examDuration) * 100;
  
  if (percentage > 50) return 'text-state-nominal';
  if (percentage > 25) return 'text-content-primary';
  if (percentage > 10) return 'text-state-warning';
  return 'text-state-critical';
};

// Warning thresholds
export const getTimeWarning = (timeRemaining: number): string | null => {
  if (timeRemaining <= 60) return 'Less than 1 minute remaining!';
  if (timeRemaining <= 300) return '5 minutes remaining';
  if (timeRemaining <= 600) return '10 minutes remaining';
  return null;
};

// Cleanup function to be called when component unmounts
export const cleanupExamStore = () => {
  const { stopTimer, disableAutoSave } = useExamStore.getState();
  stopTimer();
  disableAutoSave();
};